import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { useImmer } from 'use-immer';
import { WithCitizenContext } from '../../citizen/citizen-context';
import { AttendanceStatisticsComponent } from './attendance-statistics-component';
import { moduleClientFactory } from '../../../api-service/module-client-factory';
import { AttendanceStatisticsCourse, AttendanceStatisticsErrors, AttendanceStatisticsState } from './interfaces';
import { toFirstDayOfMonth, toLastDayOfMonth } from '../../../utilities';
import { CourseDtoEx, CourseEnrollmentLessonDto, CourseEnrollmentListDto, LessonStateDto } from '../../../api-service/clients';
import { DatePickerValue } from '../../../components/date-picker';

const attendanceClient = moduleClientFactory.createAttendanceClient();

export function AttendanceStatistics() {
  const { citizenId, courseId } = useParams();
  const isCourseScoped = !!courseId;

  const [state, setState] = useImmer<AttendanceStatisticsState>({
    courses: [],
    selectedCourses: [],
    fromDate: toFirstDayOfMonth(new Date()),
    toDate: toLastDayOfMonth(new Date()),
    includeFutureLessons: false,
  });
  const [errors, setErrors] = useState<AttendanceStatisticsErrors>({});
  const [courseName, setCourseName] = useState<string>();

  const [isLoading, setIsLoading] = useState(true);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [isLoadingStatistics, setIsLoadingStatistics] = useState(false);
  const [enrollmentLessons, setEnrollmentLessons] = useState<CourseEnrollmentLessonDto[]>([]);
  const [defaultIncludeFutureLessons, setDefaultIncludeFutureLessons] = useState(false);

  useEffect(() => {
    loadAttendanceStatisticFilterValues();
  }, []);

  async function loadAttendanceStatisticFilterValues() {
    try {
      setIsLoading(true);

      if (isCourseScoped) {
        const [courseResult, includeFutureLessonsResult] = await Promise.all([
          attendanceClient.getCourseById({ courseId }),
          attendanceClient.getIncludeFutureLessonsTenantConfiguration(),
        ]);

        setCourseName(courseResult.course?.name);
        setDefaultIncludeFutureLessons(includeFutureLessonsResult.includeFutureLessons);
        setState(draft => {
          draft.includeFutureLessons = includeFutureLessonsResult.includeFutureLessons;
        });
        return;
      }

      const [activeCoursesResult, courseEnrollmentsResult, includeFutureLessonsResult, enrollmentLessonsResult] = await Promise.all([
        attendanceClient.getActiveCourses({}),
        attendanceClient.getCourseEnrollmentsByCitizenId({ citizenId }),
        attendanceClient.getIncludeFutureLessonsTenantConfiguration(),
        attendanceClient.getCourseEnrollmentLessonsByCitizenId({ citizenId }),
      ]);

      const lessons = enrollmentLessonsResult.lessons || [];
      const enrollmentCourses = toCourseOptions(activeCoursesResult.courses || [], courseEnrollmentsResult.courseEnrollments || [], lessons);

      setEnrollmentLessons(lessons);
      setDefaultIncludeFutureLessons(includeFutureLessonsResult.includeFutureLessons);
      setState(draft => {
        draft.courses = enrollmentCourses;
        draft.includeFutureLessons = includeFutureLessonsResult.includeFutureLessons;
        draft.selectedCourses = getCoursesWithLessonsInPeriod(enrollmentCourses, lessons, draft.fromDate, draft.toDate);
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  }

  const { courses, selectedCourses, fromDate, toDate, lessons, summary, includeFutureLessons } = state;

  function handleAttendanceFilterChange<K extends keyof AttendanceStatisticsState>(name: K, value: AttendanceStatisticsState[K]) {
    setState(draft => {
      draft[name] = value;
    });
  }

  function reportPeriodErrors() {
    const tmpErrors = {} as AttendanceStatisticsErrors;
    if (!fromDate) {
      tmpErrors.fromDate = 'Startdato skal angives';
    }

    if (!toDate) {
      tmpErrors.toDate = 'Slutdato skal angives';
    }
    setErrors({ ...tmpErrors });
  }

  async function handleShowStatistics() {
    try {
      reportPeriodErrors();
      if (!fromDate || !toDate) {
        return;
      }

      setIsLoadingStatistics(true);
      const result = isCourseScoped
        ? await attendanceClient.getCourseAttendanceStatistics({
            courseId,
            from: fromDate,
            to: toDate,
            includeFutureLessons,
          })
        : await attendanceClient.getCitizenAttendanceStatistics({
            citizenId,
            from: fromDate,
            to: toDate,
            courseIds: selectedCourses.map(x => x.courseId),
            includeFutureLessons,
          });

      setState(draft => {
        draft.lessons = result.lessons;
        draft.summary = result.summary;
      });
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingStatistics(false);
    }
  }

  async function handleCreatePdf() {
    try {
      reportPeriodErrors();
      if (!fromDate || !toDate) {
        return;
      }

      setIsGeneratingPdf(true);
      const result = isCourseScoped
        ? await attendanceClient.getCourseAttendanceStatisticsPdf({
            courseId,
            from: fromDate,
            to: toDate,
            includeFutureLessons,
          })
        : await attendanceClient.getCitizenAttendanceStatisticsPdf({
            citizenId,
            from: fromDate,
            to: toDate,
            courseIds: selectedCourses.map(x => x.courseId),
            includeFutureLessons,
          });

      const pdfDocumentBlob = new Blob([result.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfDocumentBlob);
      const link = document.createElement('a');
      link.href = url;
      link.target = '_blank';
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error(error);
    } finally {
      setIsGeneratingPdf(false);
    }
  }

  function handleResetFilters() {
    const resetFromDate = toFirstDayOfMonth(new Date());
    const resetToDate = toLastDayOfMonth(new Date());

    setErrors({});
    setState(draft => {
      draft.fromDate = resetFromDate;
      draft.toDate = resetToDate;
      draft.includeFutureLessons = defaultIncludeFutureLessons;
      draft.selectedCourses = getCoursesWithLessonsInPeriod(courses, enrollmentLessons, resetFromDate, resetToDate);
      draft.lessons = [];
      draft.summary = undefined;
    });
  }

  const statisticsComponent = (
    <AttendanceStatisticsComponent
      isCourseScoped={isCourseScoped}
      courseName={courseName}
      courses={courses}
      selectedCourses={selectedCourses}
      onAttendanceFilterChange={handleAttendanceFilterChange}
      errors={errors}
      fromDate={fromDate}
      toDate={toDate}
      onShowStatistics={handleShowStatistics}
      lessons={lessons}
      summary={summary}
      onReset={handleResetFilters}
      onCreatePdf={handleCreatePdf}
      isLoading={isLoading || isLoadingStatistics}
      isGeneratingPdf={isGeneratingPdf}
      includeFutureLessons={includeFutureLessons}
    />
  );

  if (isCourseScoped) {
    return statisticsComponent;
  }

  return <WithCitizenContext citizenId={citizenId}>{statisticsComponent}</WithCitizenContext>;
}

// Compares on calendar day so a lesson on the last day of the period still counts.
function toDayValue(value: Date | string) {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function getCoursesWithLessonsInPeriod(courses: AttendanceStatisticsCourse[], enrollmentLessons: CourseEnrollmentLessonDto[], fromDate: DatePickerValue, toDate: DatePickerValue) {
  if (!fromDate || !toDate) {
    return [];
  }

  const from = toDayValue(fromDate);
  const to = toDayValue(toDate);
  const courseIdsInPeriod = new Set(
    enrollmentLessons
      .filter(lesson => lesson.lessonState !== LessonStateDto.Cancelled)
      .filter(lesson => toDayValue(lesson.beginAt) >= from && toDayValue(lesson.beginAt) <= to)
      .map(lesson => lesson.courseId),
  );

  return courses.filter(course => courseIdsInPeriod.has(course.courseId));
}

// A completed course is not among the active courses, so the citizen's lessons supply the rest of the enrolled courses.
function toCourseOptions(activeCourses: CourseDtoEx[], courseEnrollments: CourseEnrollmentListDto[], enrollmentLessons: CourseEnrollmentLessonDto[]) {
  const enrolledCourseIds = new Set(courseEnrollments.map(enrollment => enrollment.courseId));
  const activeEnrolledCourses = activeCourses.filter(course => enrolledCourseIds.has(course.courseId));

  const coursesById = new Map<string, AttendanceStatisticsCourse>();
  enrollmentLessons.forEach(lesson => coursesById.set(lesson.courseId, { courseId: lesson.courseId, name: lesson.courseName }));
  activeEnrolledCourses.forEach(course => coursesById.set(course.courseId, { courseId: course.courseId, name: course.name }));

  return [...coursesById.values()].sort((first, second) => first.name.localeCompare(second.name, 'da'));
}
